<template>
    <div class="flex flex-col h-[75vh]">
        <!-- Horizontal Header -->
        <div class="pb-4 border-b border-surface-200">
            <div class="flex items-center gap-4">
                <div class="flex items-center gap-2 shrink-0">
                    <span class="material-icons text-surface-400">assignment</span>
                    <label for="name" class="text-xs font-black text-surface-900 uppercase tracking-widest">Nome da Lista</label>
                </div>
                <div class="flex-1 p-fluid relative">
                    <InputText 
                        id="name" 
                        v-model.trim="name" 
                        :readonly="readOnly" 
                        required 
                        autofocus 
                        :class="{ 'p-invalid': submitted && !name }" 
                        placeholder="Ex: CNAEs de Alto Risco"
                        class="!rounded-lg !border-surface-300 focus:!border-blue-500 !bg-white !py-2 !text-base !font-bold"
                    />
                    <small class="p-error flex items-center gap-1 mt-1 font-medium absolute left-0 -bottom-5" v-if="submitted && !name">
                        <span class="material-icons text-xs">error</span>
                        O nome é obrigatório.
                    </small>
                </div>
            </div>
        </div>

        <!-- Main Content Area: Two Columns -->
        <div class="flex-1 flex overflow-hidden py-4 gap-6">
            
            <!-- Left Column: Addition Methods -->
            <div class="w-1/2 flex flex-col gap-4 overflow-y-auto pr-2">
                <h3 class="text-xs font-black text-surface-400 uppercase tracking-widest mb-2 px-1">Métodos de Adição</h3>
                
                <!-- Method 1: Manual -->
                <div class="bg-white border border-surface-200 rounded-2xl p-4 shadow-sm hover:border-blue-200 transition-colors">
                    <h4 class="text-sm font-bold text-surface-700 mb-3 flex items-center gap-2">
                        <span class="material-icons text-blue-500">edit</span>
                        Individual
                    </h4>
                    <div class="field">
                        <label for="cnae" class="text-[10px] font-bold text-surface-400 uppercase mb-1 block">Pesquisar Código ou Descrição</label>
                        <div class="flex gap-2">
                            <AutoComplete v-model="cnaeToAdd" fluid :suggestions="suggestions" @complete="search" @item-select="addCnae" optionLabel="code" placeholder="Ex: 6201-5/01" class="flex-grow">
                                <template #option="slotProps">
                                    <div class="text-xs">
                                        <span class="font-bold">{{ formatCnae(slotProps.option.code) }}</span> - {{ slotProps.option.description }}
                                    </div>
                                </template>
                            </AutoComplete>
                            <Button @click="addCnae" :disabled="isCnaeAddButtonDisabled" severity="secondary">
                                <template #icon><span class="material-icons">add</span></template>
                            </Button>
                        </div>
                        <small v-if="cnaeDescription" class="text-blue-600 font-medium block mt-2 px-2 py-1 bg-blue-50 rounded text-[11px] border border-blue-100 italic">
                            "{{ cnaeDescription }}"
                        </small>
                    </div>
                </div>

                <!-- Method 2: CSV -->
                <div v-if="!readOnly" class="bg-white border border-surface-200 rounded-2xl p-4 shadow-sm hover:border-green-200 transition-colors">
                    <h4 class="text-sm font-bold text-surface-700 mb-3 flex items-center gap-2">
                        <span class="material-icons text-green-500">upload_file</span>
                        Importação CSV
                    </h4>
                    
                    <div class="bg-surface-50 p-3 rounded-xl border border-surface-100 mb-4 text-[11px] leading-relaxed text-surface-600">
                        <div class="flex items-center gap-2 mb-2">
                            <span class="material-icons text-blue-500 text-xs">info</span>
                            <span class="font-bold text-surface-900 uppercase tracking-tighter text-[10px]">Guia de Estrutura:</span>
                        </div>
                        <ul class="list-disc ml-4 space-y-1">
                            <li>Formato <strong>.csv</strong> com códigos numéricos.</li>
                            <li>Aceita um código por linha ou separados por vírgula.</li>
                            <li>Códigos inválidos são filtrados automaticamente.</li>
                        </ul>
                    </div>

                    <FileUpload
                        name="cnae_csv"
                        accept=".csv"
                        :customUpload="true"
                        @uploader="handleFileUpload"
                        mode="basic"
                        auto
                        class="p-button-secondary p-button-outlined w-full"
                        chooseLabel="Carregar Arquivo"
                    >
                        <template #uploadicon>
                            <span class="material-icons mr-2">cloud_upload</span>
                        </template>
                    </FileUpload>
                </div>
            </div>

            <!-- Vertical Divider -->
            <div class="w-px bg-surface-200 self-stretch my-2"></div>

            <!-- Right Column: Table / Preview -->
            <div class="w-1/2 flex flex-col overflow-hidden">
                <div class="flex items-center justify-between mb-3 px-1">
                    <h3 class="text-xs font-black text-surface-400 uppercase tracking-widest">Itens Selecionados</h3>
                    <div class="flex items-center gap-2">
                        <span class="text-[10px] font-bold text-surface-400 uppercase">Total:</span>
                        <span class="px-2.5 py-0.5 bg-surface-900 text-white text-[10px] font-black rounded-full">{{ cnaeItems.length }}</span>
                    </div>
                </div>
                
                <div class="flex-1 bg-white border border-surface-200 rounded-2xl overflow-hidden shadow-sm flex flex-col">
                    <DataTable :value="cnaeItems" paginator :rows="7" scrollable scrollHeight="flex" responsiveLayout="scroll" emptyMessage="Nenhum código adicionado." class="p-datatable-sm h-full">
                        <Column header="Código" style="width: 30%">
                            <template #body="slotProps">
                                <span class="font-mono font-bold text-surface-900 bg-surface-50 px-1.5 py-0.5 rounded border border-surface-100 text-xs">{{ formatCnae(slotProps.data.cnae_code) }}</span>
                            </template>
                        </Column>
                        <Column header="Descrição">
                            <template #body="slotProps">
                                <span class="text-xs text-black leading-tight line-clamp-2" :title="cnaeDescriptions[slotProps.data.cnae_code]?.description">
                                    {{ cnaeDescriptions[slotProps.data.cnae_code]?.description || 'N/A' }}
                                </span>
                            </template>
                        </Column>
                        <Column v-if="!readOnly" header="" style="width: 3rem; text-align: right;">
                            <template #body="slotProps">
                                <Button class="p-button-rounded p-button-danger p-button-text p-0 w-7 h-7" @click="removeCnae(slotProps.index)">
                                    <template #icon><span class="material-icons text-base">delete</span></template>
                                </Button>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- Fixed Footer -->
        <div class="pt-4 border-t border-surface-200 flex justify-end gap-3 mt-2">
            <template v-if="!readOnly">
                <Button label="Cancelar" class="p-button-text text-surface-500 font-bold px-6" @click="closeDialog()">
                    <template #icon><span class="material-icons">close</span></template>
                </Button>
                <span v-tooltip.top="saveTooltip">
                    <Button 
                        label="Salvar" 
                        class="px-8 !rounded-lg font-bold shadow-lg shadow-blue-900/10" 
                        @click="saveList"
                        :disabled="isSaveDisabled"
                    >
                        <template #icon><span class="material-icons">check</span></template>
                    </Button>
                </span>
            </template>
            <template v-else>
                <Button label="Fechar" class="p-button-text font-bold px-8" @click="closeDialog()">
                    <template #icon><span class="material-icons">close</span></template>
                </Button>
            </template>
        </div>
    </div>

    <!-- Custom Unsaved Changes Dialog -->
    <Dialog v-model:visible="showUnsavedChangesDialog" header="Alterações não salvas" :modal="true" :draggable="false" style="width: 400px">
        <div class="flex items-center gap-4 py-2">
            <div class="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center shrink-0 border border-amber-100 shadow-sm">
                <span class="material-icons text-amber-500 text-3xl">warning</span>
            </div>
            <div class="flex flex-col gap-1">
                <span class="font-bold text-surface-900">Confirmar Saída?</span>
                <span class="text-xs text-surface-500 leading-relaxed">Você possui alterações pendentes na lista. Ao sair agora, todo o progresso não salvo será perdido permanentemente.</span>
            </div>
        </div>
        <template #footer>
            <Button label="Voltar e Salvar" class="p-button-text text-surface-600 text-[11px] font-bold uppercase tracking-wider" @click="showUnsavedChangesDialog = false">
                <template #icon><span class="material-icons text-sm">edit</span></template>
            </Button>
            <Button label="Descartar e Sair" class="p-button-danger p-button-text text-[11px] font-black uppercase tracking-widest" @click="confirmClose">
                <template #icon><span class="material-icons text-sm">logout</span></template>
            </Button>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useSystemCnaeListStore } from '@/stores/systemCnaeList';
import { useAccountCnaeListStore } from '@/stores/accountCnaeList';
import type { CnaeList, CnaeItemInput } from '@/types/cnaeList';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import FileUpload, { type FileUploadUploaderEvent } from 'primevue/fileupload';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import cnaeDescriptionsJson from '@/assets/cnae_descriptions.json';
import AutoComplete, { type AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import { formatCnae, stripCnaeFormat } from '@/utils/cnae';
import { type CnaeDescription } from '@/types/cnaeList'

const cnaeDescriptions: Record<string, CnaeDescription> = cnaeDescriptionsJson;

const props = defineProps({
    cnaeList: { type: Object as () => CnaeList | null, default: null },
    readOnly: { type: Boolean, default: false },
    scope: {
        type: String,
        required: true,
        validator: (value: string) => ['system', 'account'].includes(value)
    }
});
const emit = defineEmits(['close']);

const cnaeListStore = props.scope === 'system' ? useSystemCnaeListStore() : useAccountCnaeListStore();
const toast = useToast();

const name = ref('');
const cnaeItems = ref<CnaeItemInput[]>([])
const cnaeToAdd = ref<string | { code: string; description: string } | null>(null);
const submitted = ref(false);
const suggestions = ref<{ code: string; description: string }[]>([]);
const showUnsavedChangesDialog = ref(false);

const initialData = ref({ name: '', items: [] as string[] });

const isSaveDisabled = computed(() => {
    return !name.value.trim() || cnaeItems.value.length === 0;
});

const saveTooltip = computed(() => {
    if (!name.value.trim() && cnaeItems.value.length === 0) {
        return 'Informe o nome e adicione pelo menos 1 código CNAE';
    }
    if (!name.value.trim()) {
        return 'O nome da lista é obrigatório';
    }
    if (cnaeItems.value.length === 0) {
        return 'Adicione pelo menos 1 código CNAE à lista';
    }
    return '';
});

const isDirty = computed(() => {
    if (props.readOnly) return false;

    const currentName = name.value;
    const currentItems = cnaeItems.value.map(item => stripCnaeFormat(item.cnae_code)).sort();

    const hasNameChanged = currentName !== initialData.value.name;
    const hasItemsChanged = JSON.stringify(currentItems) !== JSON.stringify(initialData.value.items);

    return hasNameChanged || hasItemsChanged;
});

const cnaeData = Object.entries(cnaeDescriptions).map(([code, data]) => ({ code, ...data }));

const search = (event: AutoCompleteCompleteEvent) => {
    if (!event.query.trim().length) {
        suggestions.value = [];
        return;
    }
    const query = event.query.toLowerCase();
    const strippedQuery = stripCnaeFormat(query);

    suggestions.value = cnaeData.filter((cnae) => {
        const matchesDescription = cnae.description.toLowerCase().includes(query);
        const matchesCode = strippedQuery.length > 0 && cnae.code.includes(strippedQuery);
        return matchesDescription || matchesCode;
    });
}

const getCnaeInputCode = (): string => {
    let code = '';
    if (typeof cnaeToAdd.value === 'string') {
        code = cnaeToAdd.value;
    } else if (typeof cnaeToAdd.value === 'object' && cnaeToAdd.value !== null) {
        code = cnaeToAdd.value.code;
    }
    return stripCnaeFormat(code);
};

const cnaeDescription = computed(() => {
    const code = getCnaeInputCode();
    if (code && Object.hasOwn(cnaeDescriptions, code)) {
        return cnaeDescriptions[code]?.description || 'CNAE não encontrado';
    }
    return '';
});

const isCnaeAddButtonDisabled = computed(() => {
    if (props.readOnly) return true;
    const code = getCnaeInputCode();
    return !(code && Object.hasOwn(cnaeDescriptions, code));
});

watch(props, (newProps) => {
    if (newProps.cnaeList) {
        name.value = newProps.cnaeList.name;
        const items = newProps.cnaeList.items
            .filter(x => !x.deleted_at)
            .map(item => stripCnaeFormat(item.cnae_code));
        
        cnaeItems.value = items.map(code => ({ cnae_code: code }));
        
        initialData.value = {
            name: name.value,
            items: [...items].sort()
        };
    } else {
        name.value = '';
        cnaeItems.value = [];
        initialData.value = { name: '', items: [] };
    }
}, { immediate: true });

const addCnae = () => {
    const codeToAdd = getCnaeInputCode();

    if (codeToAdd && Object.hasOwn(cnaeDescriptions, codeToAdd)) {
        if (!cnaeItems.value.some(item => item.cnae_code === codeToAdd)) {
            cnaeItems.value.push({ cnae_code: codeToAdd });
            cnaeToAdd.value = null;
        } else {
            toast.add({ severity: 'warn', summary: 'CNAE Duplicado', detail: 'Este código CNAE já está na lista.', life: 3000 });
        }
    } else {
        toast.add({ severity: 'error', summary: 'CNAE Inválido', detail: 'Por favor, selecione um código CNAE válido da lista.', life: 3000 });
    }
};

const removeCnae = (index: number) => {
    cnaeItems.value.splice(index, 1);
};

const handleFileUpload = (event: FileUploadUploaderEvent) => {
    let file: File | undefined;
    if (Array.isArray(event.files)) {
        file = event.files[0];
    } else {
        file = event.files;
    }
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
        const text = e.target?.result as string;
        const allCodesInFile = text.split(/[\s,]+/).map(code => stripCnaeFormat(code.trim())).filter(code => code !== '');

        let validNewCodes: string[] = [];
        let invalidCodes: string[] = [];
        let duplicateCodes: string[] = [];

        const currentCnaeItems = cnaeItems.value.map(item => item.cnae_code);

        allCodesInFile.forEach(code => {
            if (Object.hasOwn(cnaeDescriptions, code)) {
                if (!currentCnaeItems.includes(code) && !validNewCodes.includes(code)) {
                    validNewCodes.push(code);
                } else {
                    duplicateCodes.push(code);
                }
            } else {
                invalidCodes.push(code);
            }
        });

        validNewCodes.forEach(code => {
            cnaeItems.value.push({ cnae_code: code });
        });

        if (validNewCodes.length > 0) {
            toast.add({ severity: 'success', summary: 'Upload bem-sucedido', detail: `${validNewCodes.length} novos códigos CNAE adicionados.`, life: 3000 });
        }

        if (invalidCodes.length > 0) {
            toast.add({ severity: 'error', summary: 'Códigos Inválidos Ignorados', detail: `${invalidCodes.length} códigos foram ignorados devido a formato inválido ou não terem sido encontrados.`, life: 5000 });
        }

        if (duplicateCodes.length > 0) {
            toast.add({ severity: 'warn', summary: 'Códigos Duplicados Ignorados', detail: `${duplicateCodes.length} códigos foram ignorados por já existirem.`, life: 5000 });
        }
    };
    reader.readAsText(file);
};

const saveList = async () => {
    submitted.value = true;
    if (!name.value) {
        return;
    }

    const cnaeItemsToSave = cnaeItems.value.map(item => ({ cnae_code: stripCnaeFormat(item.cnae_code) }));

    if (props.cnaeList) {
        if (props.cnaeList.name !== name.value) {
            await cnaeListStore.updateCnaeList(props.cnaeList.id, { name: name.value });
        }

        const originalItems = props.cnaeList.items
            .filter(i => !i.deleted_at)
            .map(i => stripCnaeFormat(i.cnae_code));
        const newItems = cnaeItemsToSave.map(i => i.cnae_code);

        const itemsToAdd = newItems.filter(newItemCode => !originalItems.includes(newItemCode));
        if (itemsToAdd.length > 0) {
            await cnaeListStore.addCnaesToList(props.cnaeList.id, itemsToAdd);
        }

        const originalItemsMap = new Map(
            props.cnaeList.items
                .filter(i => !i.deleted_at)
                .map(i => [stripCnaeFormat(i.cnae_code), i.id])
        );
        const itemsToRemoveIds = Array.from(originalItemsMap.keys())
            .filter(origCode => !newItems.includes(origCode))
            .map(code => originalItemsMap.get(code))
            .filter((id): id is string => id !== undefined);


        if (itemsToRemoveIds.length > 0) {
            await cnaeListStore.removeCnaesFromList(props.cnaeList.id, itemsToRemoveIds);
        }

    } else {
        const listData = {
            name: name.value,
            is_template: props.scope === 'system',
            items: cnaeItemsToSave,
        };
        await cnaeListStore.createCnaeList(listData);
    }
    confirmClose();
};

const closeDialog = () => {
    if (isDirty.value) {
        showUnsavedChangesDialog.value = true;
    } else {
        confirmClose();
    }
};

const confirmClose = () => {
    emit('close');
    submitted.value = false;
    showUnsavedChangesDialog.value = false;
};

</script>
